const shaders = [
  {
    name: 'GaussianBlur',
    paramsTypes: { resolution: 'vec2' },
    defaultParams: { resolution: [ 100, 100 ] },
    glsl: `
      const int radius = 9;
      const float pi = 3.14159265359;
      
      uniform vec2 resolution; // = vec2(100, 100);
      
      float gaussian(float x, float y, float k, float n) {
        return n * exp(-(x * x + y * y) / k);
      }
      
      vec4 gaussianBlur(vec2 uv, float sigma, bool useFrom) {
        float k = 2.0 * sigma * sigma;
        float n = 1.0 / sqrt(k * pi);
        vec2 offset = vec2(1.0 / resolution.x, 1.0 / resolution.y) * sigma;
        
        vec4 color = vec4(0.0);
        float totalWeight = 0.0;
      
        for (int x = -radius; x <= radius; ++x) {
          for (int y = -radius; y <= radius; ++y) {
            vec2 offset = vec2(float(x), float(y)) * offset;
            float weight = gaussian(float(x), float(y), k, n);
      
            vec4 sample = useFrom
              ? getFromColor(uv + offset)
              : getToColor(uv + offset);
      
            color += sample * weight;
            totalWeight += weight;
          }
        }
      
        return color / totalWeight;
      }
      
      vec4 transition(vec2 uv) {
        float sigmaFrom = mix(0.5, 6.0, progress);
        float sigmaTo = mix(6.0, 0.5, progress);
      
        vec4 blurredFrom = gaussianBlur(uv, sigmaFrom, true);
        vec4 blurredTo = gaussianBlur(uv, sigmaTo, false);
      
        return mix(blurredFrom, blurredTo, progress);
      }
    `,
  },
  {
    name: 'dip',
    paramsTypes: { color: 'vec3' },
    defaultParams: { color: [ 0, 0, 0 ] },
    glsl: `
      uniform vec3 color; // = vec3(0.0)

      vec4 transition (vec2 uv) {
        if (progress < 0.5) {
          return mix(getFromColor(uv), vec4(color, 1.0), smoothstep(0.0, 0.5, progress));
        }
        
        if (progress == 0.5) {
          return vec4(color, 1.0);
        }
        
        if (progress > 0.5) {
          return mix(vec4(color, 1.0), getToColor(uv), smoothstep(0.5, 1.0, progress));
        }
      }
    `,
  },
  {
    name: 'chromakey',
    paramsTypes: {
      keyColor: 'vec3',
      similarity: 'float',
      smoothness: 'float',
      spill: 'float',
      opacity: 'float',
    },
    defaultParams: {
      keyColor: [ 0, 0, 0 ],
      similarity: 0.4,
      smoothness: 0.08,
      spill: 0.1,
      opacity: 1.0,
    },
    glsl: `
      precision mediump float;
      
      uniform sampler2D tex;
      uniform float texWidth;
      uniform float texHeight;
      
      uniform vec3 keyColor;
      uniform float similarity;
      uniform float smoothness;
      uniform float spill;
      uniform float opacity;
      
      const mat4 yuv_mat = mat4( 0.182586,  0.614231,  0.062007, 0.062745,
        -0.100644, -0.338572,  0.439216, 0.501961,
         0.439216, -0.398942, -0.040274, 0.501961,
         0.000000,  0.000000,  0.000000, 1.000000);
      
      vec2 RGB2UV(vec3 rgb){
        vec4 yuvx = vec4(rgb, 1.0)*yuv_mat;
        return yuvx.yz;
      }
      
      vec4 sampleTexture(vec2 uv) {
        return texture2D(tex, uv);
      }
      
      float getChromaDist(vec3 rgb) {
        return distance(RGB2UV(keyColor), RGB2UV(rgb));
      }
      
      float getBoxFilteredChromaDist(vec3 rgb, vec2 texCoord) {
        vec2 pixel_size = vec2(1.0 / texWidth, 1.0 / texHeight);
        vec2 h_pixel_size = pixel_size / 4.0;
        vec2 point_0 = vec2(pixel_size.x, h_pixel_size.y);
        vec2 point_1 = vec2(h_pixel_size.x, -pixel_size.y);
        float distVal = getChromaDist(sampleTexture(texCoord-point_0).rgb);
        distVal += getChromaDist(sampleTexture(texCoord+point_0).rgb);
        distVal += getChromaDist(sampleTexture(texCoord-point_1).rgb);
        distVal += getChromaDist(sampleTexture(texCoord+point_1).rgb);
        distVal *= 2.0;
        distVal += getChromaDist(rgb);
        return distVal / 9.0;
      }
      
      vec4 ProcessChromaKey(vec4 rgba, vec2 texCoord) {
        float chromaDist = getBoxFilteredChromaDist(rgba.rgb, texCoord);
      
        float baseMask = chromaDist - similarity;
        float fullMask = pow(clamp(baseMask / smoothness, 0., 1.), 1.5);
        float spillVal = pow(clamp(baseMask / spill, 0., 1.), 1.5);
      
        rgba.a *= opacity;
        rgba.a *= fullMask;
      
        float desat = (rgba.r * 0.2126 + rgba.g * 0.7152 + rgba.b * 0.0722);
        rgba.rgb = clamp(vec3(desat, desat, desat),0., 1.) * (1.0 - spillVal) + rgba.rgb * spillVal;
      
        return rgba;
      }
      
      void main(void) {
        vec2 texCoord = vec2(gl_FragCoord.x/texWidth, 1.0 - (gl_FragCoord.y/texHeight));
        vec4 rgba = sampleTexture(texCoord);
        rgba.rgb = max(vec3(0.0, 0.0, 0.0), rgba.rgb / rgba.a);
        gl_FragColor = ProcessChromaKey(rgba, texCoord);
      }
    `,
  },
]

export default shaders
